import React, { useEffect, useState } from "react";
import Select from "react-select";
import "./LearnerBySession.scss";
import withAPI from "../../../../services/api";
import GroupedLessonsSelect from "./GroupedLessonsSelect";
import LearnerBySessionCarousel from "./LearnerBySessionCarousel";
import FA from "../../../../containers/fa";
import config from "../../../../common/config";
import LessonContextTextInfo from "../../LessonContextTextInfo";
import { compose } from "redux";
import { connect } from "react-redux";
import PdfDownloadBtn from "../../ClassTab/PdfDownloadBtn";
import NoGroupedLessonsErrMsg from "../ReportsErrMsgs/NoGroupedLessonsErrMsg";

import { createLessonContextInfo } from "../../ClassTab/LearnerScoresTable/LearnerScoresTableUtilities";

/**
 * Finds lesson data by ID from the lessons array
 * @param {string} lessonId - The lesson ID to find
 * @param {Array} lessons - The lessons array from backend
 * @returns {Object|null} - The lesson data or null if not found
 */
const findLessonDataById = (lessonId, lessons) => {
  for (const group of lessons) {
    if (group.options) {
      for (const lesson of group.options) {
        if (lesson.value === lessonId) {
          return lesson;
        }
      }
    }
  }
  return null;
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  robotType: state.status.robotType,
  homeClassId: state.homeVersion.homeClassId,
});

const LearnerBySession = ({
  error,
  ready,
  activeLearner,
  api,
  user,
  setShowOverlay,
}) => {
  const isHomeVersion =
    user && user.organization.category === config.ABII_HOME.toLowerCase();

  const [fetchingLessons, setFetchingLessons] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [pairings, setPairings] = useState({});
  const [fetchingLessonsErr, setFetchingLessonsErr] = useState(null);
  const [lessonInstances, setLessonInstances] = useState([]);
  const [selectedLessonInstance, setSelectedLessonInstance] = useState("");
  const [lessonContextInfo, setLessonContextInfo] = useState(null);
  const [selectedLessonName, setSelectedLessonName] = useState("");
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [classAverageData, setClassAverageData] = useState(null);
  const [learnerLessonReport, setLearnerLessonReport] = useState(null);
  const [attentionNotRecorded, setAttentionNotRecorded] = useState(false);
  const [learnerFetchErr, setLearnerFetchErr] = useState(null);
  const [noGroupLessons, setNoGroupLessons] = useState(false);
  const [preferredStandardSet, setPreferredStandardSet] = useState(null);
  const [latestLessonId, setLatestLessonId] = useState(null);

  useEffect(() => {
    if (!activeLearner) return;
    setFetchingLessons(true);
    setSelectedLessonId("");
    setSelectedLessonInstance("");
    setLessonContextInfo(null);
    setNoGroupLessons(false);
    setLearnerFetchErr(null);
    setLatestLessonId(null);

    api
      .fetchLessonsForLearner(activeLearner.sid)
      .then((lessons) => {
        console.log("LESSONS taken by the learner:", lessons);
        let postData = { lessons: lessons["lesson_ids"] };
        let latestLesson = lessons["latest_lesson"];
        if (latestLesson) {
          setSelectedLessonId(latestLesson);
          setLatestLessonId(latestLesson);
        }
        fetchGroupLessons(postData);
      })
      .catch((err) => {
        console.log(err.message);
        setFetchingLessons(false);
        setFetchingLessonsErr(
          `Something went wrong fetching lessons for this learner.`
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLearner]);

  useEffect(() => {
    if (lessons && lessons.length > 0 && latestLessonId) {
      console.log("Looking for lesson with ID:", latestLessonId);

      let matched = null;

      for (const group of lessons) {
        for (const lesson of group.options || []) {
          if (lesson.value === latestLessonId) {
            matched = lesson;
            break;
          }
        }
        if (matched) break;
      }

      if (matched) {
        console.log("✔️ Auto-selecting matched lesson:", matched.label);
        updateLessonData(matched.value, matched.label);
      } else {
        const fallback = lessons[0]?.options?.[0];
        if (fallback) {
          console.log(
            "⚠️ Falling back to first available lesson:",
            fallback.label
          );
          updateLessonData(fallback.value, fallback.label);
        }
      }
    }
  }, [lessons, latestLessonId]);

  const fetchGroupLessons = (postData) => {
    api
      .groupLessons(postData)
      .then((res) => {
        setFetchingLessons(false);
        setFetchingLessonsErr(null);
        if (res.grouped_lessons) {
          setLessons(res.grouped_lessons);
        } else {
          setNoGroupLessons(true);
          setLessons([]);
        }
        setPairings(res.pairings);

        if (res.preferred_standardset) {
          setPreferredStandardSet(res.preferred_standardset);
        } else if (
          user &&
          user.organization &&
          user.organization.preferredStandardSet
        ) {
          setPreferredStandardSet(user.organization.preferredStandardSet);
        }
      })
      .catch((err) => {
        setLearnerFetchErr(
          `Something went wrong fetching lessons for this learner.`
        );
        setLessons([]);
        setFetchingLessons(false);
        console.log(err);
      });
  };

  const updateLessonData = (lessonId, lessonName) => {
    setSelectedLessonId(lessonId);
    setSelectedLessonInstance("");
    setLessonInstances([]);
    setSelectedLessonName(lessonName);

    setClassAverageData(null);
    setLearnerLessonReport(null);
    setLessonContextInfo(null);

    const instancesPromise = api.fetchLearnerInstances(
      lessonId,
      activeLearner.sid
    );

    const contextPromise = api.fetchLessonReportContextData(lessonId);

    instancesPromise
      .then((instances) => {
        console.log("📚 Fetched lesson instances:", instances);
        setLessonInstances(instances);

        if (instances && instances.length > 0) {
          const firstInstance = {
            value: instances[0].lesson_uuid,
            label: `${instances[0].date} ${instances[0].time} v${instances[0].version} - ${instances[0].name}`,
            lessonName: `${instances[0].name}`,
          };

          setSelectedLessonInstance(firstInstance);

          const lessonInstance = instances[0];
          const { version, lesson_id, date, time, lesson_uuid } =
            lessonInstance;

          api
            .fetchLearnerReportsForLessonInstance(
              lesson_id,
              activeLearner.sid,
              version,
              date,
              time,
              lesson_uuid
            )
            .then((learnerReport) => {
              setLearnerLessonReport(learnerReport);
            })
            .catch((err) => {
              setFetchingLessonsErr(err.message);
            });

          api
            .fetchLearnerAverageReport(
              lesson_id,
              activeLearner.classId,
              activeLearner.sid
            )
            .then((averageReport) => {
              setClassAverageData(averageReport);
            })
            .catch((err) => {
              setFetchingLessonsErr(err.message);
            });
        }
      })
      .catch((err) => {
        setFetchingLessonsErr(err.message);
      });

    // ENHANCED CONTEXT HANDLING
    contextPromise
      .then((lci) => {
        // Find the lesson data with correct standard information
        const lessonDataWithStandards = findLessonDataById(lessonId, lessons);

        if (lessonDataWithStandards) {
          // Create enhanced lesson context info using our utility function
          const enhancedContextInfo = createLessonContextInfo(
            lessonDataWithStandards,
            lci
          );
          setLessonContextInfo(enhancedContextInfo);
        } else {
          // Fallback to original API response if lesson not found
          setLessonContextInfo(lci);
        }
      })
      .catch((err) => {
        // FALLBACK: If API fails, try to create context info from lesson data alone
        const lessonDataWithStandards = findLessonDataById(lessonId, lessons);
        if (lessonDataWithStandards) {
          const fallbackContextInfo = createLessonContextInfo(
            lessonDataWithStandards,
            {
              overall_question: "", // We don't have this from lesson data
            }
          );
          setLessonContextInfo(fallbackContextInfo);
        } else {
          setFetchingLessonsErr(err.message);
        }
      });
  };

  const updateLessonInstanceData = (e) => {
    setSelectedLessonInstance(e);
    if (!e) return;

    setClassAverageData(null);
    setLearnerLessonReport(null);

    const lessonInstance = lessonInstances.find(
      (x) => x.lesson_uuid === e.value
    );
    if (!lessonInstance) return;

    const { version, lesson_id, date, time, lesson_uuid } = lessonInstance;

    api
      .fetchLearnerReportsForLessonInstance(
        lesson_id,
        activeLearner.sid,
        version,
        date,
        time,
        lesson_uuid
      )
      .then((learnerReport) => {
        setLearnerLessonReport(learnerReport);
      })
      .catch((err) => {
        setFetchingLessonsErr(err.message);
      });

    api
      .fetchLearnerAverageReport(
        lesson_id,
        activeLearner.classId,
        activeLearner.sid
      )
      .then((averageReport) => {
        setClassAverageData(averageReport);
      })
      .catch((err) => {
        setFetchingLessonsErr(err.message);
      });
  };

  return (
    <div id='learner_by_session_container'>
      <div className='learner_tab_header_container'>
        <h4>
          <b>Learner Performance + Attention By Session</b>
        </h4>
        <PdfDownloadBtn
          btnCaption='Download Lesson Report...'
          enableBtn={
            selectedLessonId && selectedLessonInstance && activeLearner
          }
          setShowOverlay={setShowOverlay}
          type='learnerLessonSession'
          activeLearner={activeLearner}
          selectedLessonName={selectedLessonName}
          selectedLessonInstance={selectedLessonInstance}
        />
      </div>

      <div className='learner_tab_body'>
        {!error &&
        !fetchingLessonsErr &&
        !fetchingLessons &&
        lessons &&
        lessons.length !== 0 ? (
          <div id='learner_session_options_container'>
            <div id='learner_session_options' style={{ margin: "5px" }}>
              <div style={{ margin: "5px" }}>
                <GroupedLessonsSelect
                  groupedLessons={lessons}
                  changeHandler={updateLessonData}
                  selectedValue={selectedLessonId}
                  preferredStandardSet={preferredStandardSet}
                />
              </div>

              <div style={{ margin: "5px" }}>
                <Select
                  aria-label='Select a session'
                  isSearchable={false}
                  options={lessonInstances.map((session) => ({
                    value: session.lesson_uuid,
                    label: `${session.date} ${session.time} v${session.version} - ${session.name}`,
                    lessonName: `${session.name}`,
                  }))}
                  isDisabled={!selectedLessonId || !ready}
                  onChange={updateLessonInstanceData}
                  value={selectedLessonInstance}
                  placeholder='Select a session'
                />
              </div>
            </div>

            {lessonContextInfo !== null && (
              <div
                id='lesson_context_info_container'
                className='lesson_context_info_container'
              >
                <LessonContextTextInfo
                  lessonContextInfo={lessonContextInfo}
                  fetchingLessons={fetchingLessons}
                  pairings={pairings}
                  updateLessonData={updateLessonData}
                  learnerLessonReport={learnerLessonReport}
                  isHomeVersion={isHomeVersion}
                />
              </div>
            )}
          </div>
        ) : fetchingLessons ? (
          <div>
            <FA color='gray' icon='spinner' spin /> Loading...
          </div>
        ) : noGroupLessons ? (
          <NoGroupedLessonsErrMsg />
        ) : error || fetchingLessonsErr || learnerFetchErr ? (
          <div>An error has occurred. Please try again later.</div>
        ) : null}

        {/* Only render carousel when we have all the required data */}
        <LearnerBySessionCarousel
          activeLearner={activeLearner}
          fetchingLessons={fetchingLessons}
          selectedLessonId={selectedLessonId}
          selectedLessonInstance={selectedLessonInstance}
          classAverageData={classAverageData}
          lessonContextInfo={lessonContextInfo}
          learnerLessonReport={learnerLessonReport}
          attentionNotRecorded={attentionNotRecorded}
          setAttentionNotRecorded={setAttentionNotRecorded}
        />
      </div>

      {selectedLessonInstance && (
        <div
          id='lesson_session_notes'
          className='alert-primary'
          style={{ borderRadius: "0.25rem", padding: "0.5rem" }}
        >
          <strong>Note:</strong>
          <ul>
            <li>Switch between graphs by clicking on the circles.</li>
            <li>
              Click on the data points to view the question on the line graph.
            </li>
            {lessonContextInfo?.type === "TUT" && (
              <li>
                Average statistics do not include pre-assessment question unless
                learner aced it.
              </li>
            )}
            {attentionNotRecorded && (
              <li>Attention not recorded during this lesson.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default compose(connect(mapStateToProps), withAPI)(LearnerBySession);
